import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";

import {
    getOnboardingProgress,
    saveOnboardingProgress,
    completeOnboarding,
    TOTAL_STEPS,
} from "src/Api/services/onboardingService";

// Onboarding slide images - placed in public/onboarding/
const SLIDES = [
    { src: "/onboarding/1.png", alt: "Bienvenida - Hola Coach" },
    { src: "/onboarding/2.png", alt: "Paso 1 - Completa tu perfil" },
    { src: "/onboarding/3.png", alt: "Paso 2 - Configura tu disponibilidad" },
    { src: "/onboarding/4.png", alt: "Paso 3 - Invita Clientes 1-1" },
    { src: "/onboarding/5.png", alt: "Paso 3 - Invita Grupos" },
    { src: "/onboarding/6.png", alt: "Configuración inicial lista" },
    { src: "/onboarding/7.png", alt: "Descubre el potencial de MuyCoach" },
];

function OnboardingModal() {
    const session = useSelector((state) => state.session);
    const userId = session?.user?.id;

    const [open, setOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [imageLoading, setImageLoading] = useState(true);

    // Fetch onboarding progress on mount
    useEffect(() => {
        const fetchProgress = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                const progress = await getOnboardingProgress(userId);
                if (progress.completed) {
                    setOpen(false);
                } else {
                    setCurrentStep(progress.currentStep || 0);
                    setOpen(true);
                }
            } catch (error) {
                console.error("Error fetching onboarding progress:", error);
            } finally {
                setLoading(false);
            }
        };

        if (session?.isLogged && userId) {
            fetchProgress();
        }
    }, [userId, session?.isLogged]);

    // Save progress when step changes
    const saveProgress = useCallback(
        async (step) => {
            if (!userId) return;
            await saveOnboardingProgress(userId, step);
        },
        [userId]
    );

    const handleNext = async () => {
        if (currentStep < TOTAL_STEPS - 1) {
            const nextStep = currentStep + 1;
            setCurrentStep(nextStep);
            setImageLoading(true);
            await saveProgress(nextStep);
        } else {
            // Last step - complete onboarding
            await completeOnboarding(userId);
            setOpen(false);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setImageLoading(true);
        }
    };

    const handleImageLoad = () => {
        setImageLoading(false);
    };

    // Don't render anything while loading or if not logged in
    if (loading || !session?.isLogged || !userId) return null;

    const isFirstSlide = currentStep === 0;
    const isLastSlide = currentStep === TOTAL_STEPS - 1;

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth={true}
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    maxWidth: "720px",
                    margin: "16px",
                },
            }}
            // Prevent closing by clicking backdrop or pressing Escape
            onClose={(event, reason) => {
                // Do nothing - modal cannot be closed until completed
            }}
            disableEscapeKeyDown={true}
        >
            <DialogContent
                sx={{
                    padding: 0,
                    "&:last-child": { paddingBottom: 0 },
                    position: "relative",
                    backgroundColor: "#f5f5f5",
                }}
            >
                {/* Image container */}
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        minHeight: "300px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2d1066",
                    }}
                >
                    {/* Loading spinner */}
                    {imageLoading && (
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 10,
                            }}
                        >
                            <CircularProgress
                                sx={{ color: "#ffffff" }}
                                size={40}
                            />
                        </div>
                    )}

                    {/* Slide image */}
                    <img
                        src={SLIDES[currentStep].src}
                        alt={SLIDES[currentStep].alt}
                        onLoad={handleImageLoad}
                        style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                            opacity: imageLoading ? 0 : 1,
                            transition: "opacity 0.3s ease-in-out",
                        }}
                    />
                </div>

                {/* Navigation and progress */}
                <div
                    style={{
                        backgroundColor: "white",
                        padding: "16px 24px 20px",
                    }}
                >
                    {/* Navigation buttons */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: isFirstSlide ? "center" : "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                        }}
                    >
                        {/* Back button - hidden on first slide */}
                        {!isFirstSlide && (
                            <button
                                onClick={handlePrev}
                                style={{
                                    backgroundColor: "#5b21b6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "12px",
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    letterSpacing: "1px",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#4c1d95"}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#5b21b6"}
                            >
                                ← ANTERIOR
                            </button>
                        )}

                        {/* Next / Finish button */}
                        <button
                            onClick={handleNext}
                            style={{
                                backgroundColor: "#5b21b6",
                                color: "white",
                                border: "none",
                                borderRadius: "12px",
                                padding: "10px 24px",
                                fontSize: "14px",
                                fontWeight: "700",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                letterSpacing: "1px",
                                transition: "background-color 0.2s",
                                minWidth: isLastSlide ? "160px" : "auto",
                                justifyContent: "center",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#4c1d95"}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#5b21b6"}
                        >
                            {isFirstSlide
                                ? "SIGUIENTE →"
                                : isLastSlide
                                ? "FINALIZAR ✓"
                                : "SIGUIENTE →"}
                        </button>
                    </div>

                    {/* Progress indicator dots */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        {SLIDES.map((_, index) => (
                            <div
                                key={index}
                                style={{
                                    width: index === currentStep ? "24px" : "8px",
                                    height: "8px",
                                    borderRadius: "4px",
                                    backgroundColor:
                                        index === currentStep
                                            ? "#5b21b6"
                                            : index < currentStep
                                            ? "#8b5cf6"
                                            : "#d1d5db",
                                    transition: "all 0.3s ease-in-out",
                                }}
                            />
                        ))}
                    </div>

                    {/* Step counter */}
                    <div
                        style={{
                            textAlign: "center",
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#9ca3af",
                            fontWeight: "500",
                        }}
                    >
                        {currentStep + 1} / {TOTAL_STEPS}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default OnboardingModal;
